import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

/**
 * Mark a DAMAGED pallet as REPACKED and issue a fresh child pallet in one
 * action. The child inherits weight and count from the parent; location and
 * comment can be set on the form (location defaults to the parent's).
 */
export function RepackDialog({
  palletId,
  onClose,
  onRepacked,
}: {
  palletId: string;
  onClose: () => void;
  onRepacked: (childId: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [locationRow, setLocationRow] = useState('');
  const [locationShelf, setLocationShelf] = useState('');
  const [locationPosition, setLocationPosition] = useState('');
  const [childComment, setChildComment] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(t('pallet.transition.reason_required'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await api<{ child: { id: string } }>(`/pallets/${palletId}/repack`, {
        method: 'POST',
        body: {
          reason: trimmed,
          locationRow: locationRow.trim() || undefined,
          locationShelf: locationShelf.trim() || undefined,
          locationPosition: locationPosition.trim() || undefined,
          childComment: childComment.trim() || undefined,
          ...(date ? { date } : {}),
        },
      });
      onRepacked(result.child.id);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('pallet.repack.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.op_date.label')}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.transition.reason_label')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            autoFocus
            placeholder={t('pallet.transition.hint.REPACKED')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        <div className="space-y-1">
          <span className="font-medium">{t('pallet.repack.new_location')}</span>
          <div className="grid grid-cols-3 gap-2">
            <input
              value={locationRow}
              onChange={(e) => setLocationRow(e.target.value)}
              maxLength={8}
              placeholder={t('location.row')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationShelf}
              onChange={(e) => setLocationShelf(e.target.value)}
              maxLength={8}
              placeholder={t('location.shelf')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationPosition}
              onChange={(e) => setLocationPosition(e.target.value)}
              maxLength={8}
              placeholder={t('location.position')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
          </div>
        </div>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.repack.child_comment')}</span>
          <textarea
            value={childComment}
            onChange={(e) => setChildComment(e.target.value)}
            rows={2}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('pallet.repack.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
